import React, { useEffect, useState } from 'react';
import { View, Animated, Easing } from 'react-native';
import { useRoute } from '@react-navigation/native';

import ServiceNoteButton from '../driver/components/ServiceNoteButton';
import { COINS_INDICATOR } from '../../../common/constants';
import { formatDate } from '../../../common/utils/utils';
import { Text } from '../../components';
import { Card, Screen, StatusBadge } from '../../design/components';
import useBackLabel from '../../hooks/useBackLabel';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useTheme } from '../../theme';

const CoinMechDetail = () => {
	const route = useRoute();
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const backLabel = useBackLabel();

	const {
		analysedCoinMech: {
			analysedTubes,
			mechRequiresFill,
		},
		lastCommunication,
		machineName,
	} = route.params;

	const [mechLengths] = useState(new Animated.Value(0));
	const [mechFillColor] = useState(new Animated.Value(0));

	useEffect(() => {
		Animated.sequence([
			Animated.timing(
				mechLengths,
				{
					toValue: 1,
					duration: 1000,
					easing: Easing.bounce,
					useNativeDriver: false,
				},
			),
			Animated.timing(
				mechFillColor,
				{
					toValue: 100,
					duration: 100,
					useNativeDriver: false,
				},
			),
		]).start();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Screen
			headerRight={<ServiceNoteButton />}
			backLabel={backLabel}
			heading="Coin mech levels"
			subheading={machineName}
		>
			<Card tone={mechRequiresFill ? 'warning' : 'success'} style={styles.fillCard}>
				{
					mechRequiresFill ? (
						<>
							<Text style={styles.fillHeading}>
								Fill coin mech with:
							</Text>
							{analysedTubes.map((tube, i) => {

								const {
									coinValue,
									requiresFill,
									numberOfCoinsRequired,
								} = tube;

								if (requiresFill) {
									return (
										<Text key={i} style={styles.fillListItem}>
											{'•'} {numberOfCoinsRequired} x {coinValue} coins
										</Text>
									);
								}
								return null;
							})}
						</>
					) : (
						<Text style={styles.fillHeading}>
							{ COINS_INDICATOR.NOT_REQUIRED }
						</Text>
					)
				}
			</Card>

			<Text style={styles.dataSectionText}>
				{lastCommunication
					? `Coin mech data last reported at ${formatDate(lastCommunication, 'DAY_MONTHWORD_YEAR_HOUR_MINUTE_AMPM')}`
					: 'No communication'}
			</Text>

			<View style={styles.tubesSection}>
				{analysedTubes.map((tube, i) => {

					const {
						coinValue,
						numberCoinsInTube,
						capacity,
						percentFull,
						percentFullAsInt,
						percentEmptyAsInt,
						requiresFill,
						numberOfCoinsRequired,
						ifCoinMechHasCapacities,
					} = tube;

					return (
						<View
							key={i}
							style={styles.tubeItem}
						>
							<View style={styles.coinCircle}>
								<Text style={styles.coinValue}>
									{coinValue}
								</Text>
							</View>
							<View style={styles.tubeInfo}>
								<View style={styles.tubeInfoTop}>
									<Text style={styles.tubeInfoText}>
										{numberCoinsInTube}{ifCoinMechHasCapacities && `/${capacity}`} coins
									</Text>
									{
										ifCoinMechHasCapacities && (
											<StatusBadge label={percentFull} size="small" />
										)
									}
								</View>
								<View style={styles.tubeBar}>
									<View style={styles.tubeBackground} />
									<Animated.View
										style={{
											flex: Animated.multiply(percentFullAsInt, mechLengths),
											height: 12,
											borderRadius: 6,
											backgroundColor: mechFillColor.interpolate({
												inputRange: [0, 100],
												// If requires fill, fade to the danger colour, otherwise to the progress colour
												outputRange: requiresFill ? [colors.borderStrong, colors.danger] : [colors.borderStrong, colors.progress],
											}),
										}}
									/>
									<View style={[styles.tubeEmpty, { flex: percentEmptyAsInt }]} />
								</View>
								<Text style={[
									styles.requiresFillText,
									requiresFill ? styles.requiresFillTextActive : styles.requiresFillTextInactive,
								]}>
									{
										requiresFill
											? numberOfCoinsRequired
												? `Fill ${numberOfCoinsRequired} coins`
												: 'Fill coins'
											: 'No fill required'
									}
								</Text>
							</View>
						</View>
					);
				})}
			</View>
		</Screen>
	);
};

const createStyles = ({ ui: { colors, type, space, radius } }) => ({
	fillCard: {
		gap: space.xs,
	},
	fillHeading: {
		...type.cardTitle,
		color: colors.text,
	},
	fillListItem: {
		...type.body,
		color: colors.text,
	},
	dataSectionText: {
		...type.secondary,
		color: colors.textSecondary,
	},
	tubesSection: {
		borderTopWidth: 1,
		borderTopColor: colors.border,
	},
	tubeItem: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
		paddingVertical: space.md,
		borderBottomWidth: 1,
		borderBottomColor: colors.border,
	},
	coinCircle: {
		height: 48,
		width: 48,
		borderRadius: radius.pill,
		backgroundColor: colors.chip,
		justifyContent: 'center',
		alignItems: 'center',
	},
	coinValue: {
		...type.bodySmallSemibold,
		color: colors.onChip,
	},
	tubeInfo: {
		flex: 1,
		gap: 6,
	},
	tubeInfoTop: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.sm,
	},
	tubeInfoText: {
		...type.rowTitle,
		color: colors.text,
	},
	tubeBar: {
		flexDirection: 'row',
	},
	tubeEmpty: {
		height: 12,
	},
	// Tube background fills as '100% of a tube'
	tubeBackground: {
		width: '100%',
		borderRadius: 6,
		height: 12,
		backgroundColor: colors.border,
		position: 'absolute',
	},
	requiresFillText: {
		...type.secondary,
	},
	requiresFillTextActive: {
		...type.secondarySemibold,
		color: colors.danger,
	},
	requiresFillTextInactive: {
		color: colors.textSecondary,
	},
});

export default CoinMechDetail;
